/**
 * "Pay for another" intent (GTR-4024).
 *
 * The self-registration fallback (login/register REGISTRATION_NOT_FOUND path) creates a brand
 * new participant from just an email and then joins them to whatever group_uuid is sitting in
 * storage. That join must only happen when the current participant explicitly asked to add
 * someone: they answered 'Yes' on the pay-for-another page. Any other way of landing on that
 * fallback (stale group_uuid in session storage, direct navigation to /group, an A2Z/IDP
 * exhibitor group that manages its own membership) must NOT auto-join.
 *
 * The flag is written on submission of the pay-for-another page and consumed by the join, so a
 * single 'Yes' authorizes exactly one added participant.
 */

import Container from 'typedi'
import GtrStorage from '@/modules/common/services/storage.service'

function storageKey (event_identifier: string): string {
  return `${event_identifier}_pay_for_another_intent`
}

/** Records that the participant answered 'Yes' on the pay-for-another page. */
export function setPayForAnotherIntent (event_identifier: string): void {
  Container.get(GtrStorage).setItem(storageKey(event_identifier), '1')
}

/** True when a pay-for-another 'Yes' is still outstanding for this event. */
export function hasPayForAnotherIntent (event_identifier: string): boolean {
  return !!Container.get(GtrStorage).getItem(storageKey(event_identifier))
}

/**
 * The token to send as the Bearer on the group "pay for another" add (POST participant/registration),
 * or null when this is not a group-parent add. The parent's token is the stored group parent token
 * once a member has been created, otherwise the registration token the parent's own login wrote.
 */
export function groupParentAddToken (event_identifier: string): string | null {
  const storage = Container.get(GtrStorage)
  if (!hasPayForAnotherIntent(event_identifier) || !storage.getItem(`${event_identifier}_group_uuid`)) {
    return null
  }
  return storage.getItem(`${event_identifier}_group_parent_access_token`)
    || storage.getItem(`${event_identifier}_registration_access_token`)
    || null
}

/** Consumes the intent — called after the join, and on an explicit 'No'. */
export function clearPayForAnotherIntent (event_identifier: string): void {
  Container.get(GtrStorage).removeItem(storageKey(event_identifier))
}
